import React, { useState, useEffect, useCallback } from 'react';
import { FaTrophy } from 'react-icons/fa';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import ResultModalLayout from '@/components/ResultModalLayout';
import styles from './ResultTeen9.module.css';
import "./Result.css";

const getCardImage = (cardString) => cardString ? `/assets/img/cards/${cardString}.png` : null;

// Split 9 cards into Tiger (0-2), Lion (3-5), Dragon (6-8)
const parseCards = (cardString) => {
  if (!cardString) return { tiger: [], lion: [], dragon: [] };
  const cards = cardString.split(',').map(c => c.trim());
  return {
    tiger: cards.slice(0, 3).map(c => getCardImage(c)),
    lion: cards.slice(3, 6).map(c => getCardImage(c)),
    dragon: cards.slice(6, 9).map(c => getCardImage(c)),
  };
};

// rdesc like "Lion#L : Pair"
const parseRdesc = (rdesc) => {
  if (!rdesc) return {};
  const parts = rdesc.split('#');
  const winner = parts[0] || "";
  const others = parts[1] || "";
  return { winner, others };
};

// Map win code to letter for circles
const mapWinCircle = (win) => {
  if (win === "1") return "T";
  if (win === "2") return "L";
  if (win === "3") return "D";
  return win || "";
};

const ResultTeen9 = ({ data = [], gameData = {} }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('teen9');
        if (response?.success) {
          if (response?.data?.data?.res && Array.isArray(response.data.data.res)) {
            const mapped = response.data.data.res.map(item => ({
              mid: item.mid,
              win: mapWinCircle(item.win),
            }));
            setLastResults(mapped);
          } else if (response?.data?.res && Array.isArray(response.data.res)) {
            const mapped = response.data.res.map(item => ({
              mid: item.mid,
              win: mapWinCircle(item.win),
            }));
            setLastResults(mapped);
          }
        }
      } catch (e) {
        console.error('Error fetching last results:', e);
      }
    };

    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch detail results when navigating between results
  const fetchDetailForIndex = useCallback(async (index) => {
    if (index >= 0 && index < lastResults.length && lastResults[index]?.mid) {
      try {
        const response = await getDetailResults('teen9', lastResults[index].mid.toString());
        if (response?.success) {
          if (response?.data?.data?.t1) {
            return response.data.data.t1;
          } else if (response?.data?.t1) {
            return response.data.t1;
          }
        }
      } catch (error) {
        console.error(`Error fetching detail for index ${index}:`, error);
      }
    }
    return null;
  }, [lastResults]);

  const handleResultClick = useCallback(async (resultItem, index) => {
    setSelectedResult(resultItem);
    setCurrentResultIndex(index);
    setShowModal(true);
    setLoading(true);

    const mid = resultItem?.mid;
    
    // Always fetch detail result for the clicked item
    if (mid) {
      try {
        const response = await getDetailResults('teen9', mid.toString());
        if (response?.success) {
          if (response?.data?.data?.t1) {
            setDetailResult(response.data.data.t1);
          } else if (response?.data?.t1) {
            setDetailResult(response.data.t1);
          }
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
    setCurrentResultIndex(0);
  };

  const goToPrevResult = useCallback(async () => {
    if (currentResultIndex > 0) {
      const newIndex = currentResultIndex - 1;
      setCurrentResultIndex(newIndex);
      setSelectedResult(lastResults[newIndex]);
      setLoading(true);
      const detail = await fetchDetailForIndex(newIndex);
      if (detail) {
        setDetailResult(detail);
      }
      setLoading(false);
    }
  }, [currentResultIndex, lastResults, fetchDetailForIndex]);

  const goToNextResult = useCallback(async () => {
    if (currentResultIndex < lastResults.length - 1) {
      const newIndex = currentResultIndex + 1;
      setCurrentResultIndex(newIndex);
      setSelectedResult(lastResults[newIndex]);
      setLoading(true);
      const detail = await fetchDetailForIndex(newIndex);
      if (detail) {
        setDetailResult(detail);
      }
      setLoading(false);
    }
  }, [currentResultIndex, lastResults, fetchDetailForIndex]);

  // Format modal data - memoized to prevent re-renders
  const modalData = React.useMemo(() => {
    if (!selectedResult || !showModal) return null;

    const cards = detailResult?.card ? parseCards(detailResult.card) : { tiger: [], lion: [], dragon: [] };
    const rdescData = detailResult?.rdesc ? parseRdesc(detailResult.rdesc) : {};
    const winnerName = detailResult?.winnat || rdescData.winner || "";
    const winCode = detailResult?.win;

    const isTiger = winCode === "1" || winnerName.toLowerCase().includes("tiger") || selectedResult === "T" || selectedResult?.win === "T";
    const isLion = winCode === "2" || winnerName.toLowerCase().includes("lion") || selectedResult === "L" || selectedResult?.win === "L";
    const isDragon = winCode === "3" || winnerName.toLowerCase().includes("dragon") || selectedResult === "D" || selectedResult?.win === "D";

    return {
      roundId: detailResult?.rid || gameData?.data?.data?.mid || gameData?.data?.mid || gameData?.mid || "",
      matchTime: detailResult?.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      tiger: { cards: cards.tiger, isWinner: isTiger },
      lion: { cards: cards.lion, isWinner: isLion },
      dragon: { cards: cards.dragon, isWinner: isDragon },
      resultDetails: {
        winner: winnerName,
        others: rdescData.others || "",
      },
    };
  }, [selectedResult, detailResult, showModal, gameData]);

  // Combine API results with fallback data
  const allResults = lastResults.length > 0
    ? lastResults
    : (data && data.length > 0 
        ? data.map((res, idx) => ({ win: mapWinCircle(res), mid: null }))
        : []);

  return (
    <>
      {/* Last Results Section */}
      {allResults.length > 0 && (
        <div className={styles.lastResults}>
          {allResults.map((resultItem, index) => {
            const winValue = resultItem.win || resultItem;
            const displayValue = typeof winValue === 'string' ? winValue : mapWinCircle(winValue);

            return (
              <span
                key={`${resultItem.mid || index}-${index}`}
                className={`${styles.resultCircle} ${displayValue === "T" ? styles.tiger : displayValue === "L" ? styles.lion : styles.dragon}`}
                onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
                style={{ cursor: resultItem.mid ? 'pointer' : 'default' }}
              >
                {displayValue}
              </span>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="Teenpatti Test Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div className="row">
            <h6 className="text-right round-id" style={{ marginBottom: '1rem' }}>
              <b>Round Id:</b> {modalData.roundId}
            </h6>
            <div className="col-12 player-type m-t-10 p-b-10">
              <div style={{ fontWeight: "", fontSize: "14px" }} className="text-center">Tiger</div>
              <div className="text-center">
                {modalData.tiger?.cards?.map((card, idx) => (
                  card && (
                    <img
                      style={{ width: "40px", objectFit: "contain" }}
                      key={idx}
                      src={card}
                      alt={`Tiger Card ${idx + 1}`}
                      onError={(e) => {
                        const cardName = card.split('/').pop();
                        const fallbackPath = `/assets/img/card-results/${cardName}`;
                        if (e.target.src !== fallbackPath) {
                          e.target.src = fallbackPath;
                        } else {
                          e.target.style.display = 'none';
                        }
                      }}
                    />
                  )
                ))}
              </div>
              {modalData.tiger?.isWinner && (
                <div className="winner-label m-t-10 text-center">
                  <span>
                    <FaTrophy style={{ marginRight: '5px' }} />
                    Winner
                  </span>
                </div>
              )}
            </div>

            <div className="col-12 m-t-10 p-b-10 player-type">
              <div style={{ fontWeight: "", fontSize: "14px" }} className="text-center">Lion</div>
              <div className="text-center">
                {modalData.lion?.cards?.map((card, idx) => (
                  card && (
                    <img
                      style={{ width: "40px", objectFit: "contain" }}
                      key={idx}
                      src={card}
                      alt={`Lion Card ${idx + 1}`}
                      onError={(e) => {
                        const cardName = card.split('/').pop();
                        const fallbackPath = `/assets/img/card-results/${cardName}`;
                        if (e.target.src !== fallbackPath) {
                          e.target.src = fallbackPath;
                        } else {
                          e.target.style.display = 'none';
                        }
                      }}
                    />
                  )
                ))}
              </div>
              {modalData.lion?.isWinner && (
                <div className="winner-label m-t-10 text-center">
                  <span>
                    <FaTrophy style={{ marginRight: '5px' }} />
                    Winner
                  </span>
                </div>
              )}
            </div>

            <div className="col-12 m-t-10 p-b-10 player-type">
              <div style={{ fontWeight: "", fontSize: "14px" }} className="text-center">Dragon</div>
              <div className="text-center">
                {modalData.dragon?.cards?.map((card, idx) => (
                  card && (
                    <img
                      style={{ width: "40px", objectFit: "contain" }}
                      key={idx}
                      src={card}
                      alt={`Dragon Card ${idx + 1}`}
                      onError={(e) => {
                        const cardName = card.split('/').pop();
                        const fallbackPath = `/assets/img/card-results/${cardName}`;
                        if (e.target.src !== fallbackPath) {
                          e.target.src = fallbackPath;
                        } else {
                          e.target.style.display = 'none';
                        }
                      }}
                    />
                  )
                ))}
              </div>
              {modalData.dragon?.isWinner && (
                <div className="winner-label m-t-10 text-center">
                  <span>
                    <FaTrophy style={{ marginRight: '5px' }} />
                    Winner
                  </span>
                </div>
              )}
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultTeen9;

